// KPI Overview summary strip. Two rows per the locked
// wireframe:
//
//   THIS PERIOD · {PERIOD LABEL}
//     On track   {count} of {total} KPIs
//     At risk    {count} of {total} KPIs
//     Failing    {count} of {total} KPIs
//
//   LAST {range label}
//     Compliance rate  {pct}%  [?]  {hits} of {n} months hit target
//     Failing months   {n}           across {k} KPIs · {q} in last quarter
//
// The trend row reads the calendar-month axis the compliance headline is
// computed on, so its labels name months rather than "periods" — a KPI's own
// reporting period can be a day, a week or a quarter, none of which is what
// this row counts.
//
// Combined height must stay under the user's "we don't want these to
// take up half the page" constraint — current-period row ~72px,
// trend row ~96px + overlines, well under 200px.
//
// Card styling follows the brand-skill MetricCard pattern: slate-50
// background with slate-200 border in light mode; slate-800/60 +
// slate-700 in dark mode. Replaces an earlier 4-tile single-row strip.

import type { JSX } from "react";
import { HelpCircle } from "lucide-react";
import type { PeriodRange } from "../../../../../lib/kpi/summary";

const RANGE_LABEL: Record<PeriodRange, string> = {
  "30d": "LAST 1 MONTH",
  "90d": "LAST 3 MONTHS",
  "12m": "LAST 12 MONTHS",
  ytd: "YEAR TO DATE",
};

export interface KpiSummaryStripProps {
  /** Current-period overline label, e.g. "APR 2026". */
  currentPeriodLabel: string;
  onTrack: number;
  atRisk: number;
  failing: number;
  totalKpis: number;
  /** Range-aware compliance percentage (weighted average). Null when no
   *  month in the range carries a result — rendered as a dash, never as 0%,
   *  which would state total failure where nothing has been measured. */
  overallPercent: number | null;
  /** Periods hit target / total periods in the range. */
  periodHits: number;
  periodTotal: number;
  /** Failing-period stats. */
  failingPeriods: number;
  failingPeriodKpiCount: number;
  failingInLastQuarter: number;
  /** Drives the trend row overline. Default 12m preserves the
   *  Overview panel's phase-1 behaviour for unwired callers. */
  range?: PeriodRange;
}

// Light mode uses bg-white so tiles sit visibly above the page's
// bg-slate-50 body. The previous slate-50 tile + slate-50 body combo
// rendered the tile boundary invisible against the shell. Dark mode
// keeps the translucent slate-800/60 for the same separation against
// the slate-900 body.
const CARD_CLASS =
  "rounded-md border border-slate-200 bg-white p-3.5 dark:border-slate-700 dark:bg-slate-800/60";
const OVERLINE_CLASS =
  "mb-2 text-overline uppercase tracking-wider text-slate-500 dark:text-slate-400";
const CARD_LABEL_CLASS =
  "mb-1 text-caption text-slate-500 dark:text-slate-400";
// Base typography class without colour — colour is applied per-card
// from a tone helper so threshold-driven amber / red can win over the
// neutral slate default. (Concatenating "text-slate-900 ... text-amber-600"
// loses the amber because Tailwind's CSS output orders by colour name,
// not by the order in className, so the wrong rule wins on cascade.)
const CARD_VALUE_BASE = "text-h3 leading-none";
const CARD_VALUE_NEUTRAL = "text-slate-900 dark:text-slate-100";
const CARD_VALUE_AMBER = "text-amber-600 dark:text-amber-400";
const CARD_VALUE_RED = "text-red-600 dark:text-red-400";
const CARD_SUBVALUE_CLASS =
  "mt-1 text-caption text-slate-500 dark:text-slate-400";

function atRiskValueClass(n: number): string {
  return `${CARD_VALUE_BASE} ${n > 0 ? CARD_VALUE_AMBER : CARD_VALUE_NEUTRAL}`;
}

function failingValueClass(n: number): string {
  return `${CARD_VALUE_BASE} ${n > 0 ? CARD_VALUE_RED : CARD_VALUE_NEUTRAL}`;
}

// Compliance-rate tone follows the brand on-time-rate threshold rule:
//   ≥ 90% → success (emerald) — on-cadence
//   70–89% → neutral (slate) — needs attention
//   < 70%  → warning (amber) — cadence is slipping
// Same vocabulary as the deliverable on-time tile so the user reads
// the colour cue consistently across both surfaces.
const CARD_VALUE_EMERALD = "text-emerald-600 dark:text-emerald-400";
function complianceRateValueClass(pct: number | null): string {
  if (pct === null) return `${CARD_VALUE_BASE} ${CARD_VALUE_NEUTRAL}`;
  if (pct >= 90) return `${CARD_VALUE_BASE} ${CARD_VALUE_EMERALD}`;
  if (pct >= 70) return `${CARD_VALUE_BASE} ${CARD_VALUE_NEUTRAL}`;
  return `${CARD_VALUE_BASE} ${CARD_VALUE_AMBER}`;
}

export default function KpiSummaryStrip({
  currentPeriodLabel,
  onTrack,
  atRisk,
  failing,
  totalKpis,
  overallPercent,
  periodHits,
  periodTotal,
  failingPeriods,
  failingPeriodKpiCount,
  failingInLastQuarter,
  range = "12m",
}: KpiSummaryStripProps): JSX.Element {
  const rangeOverline = RANGE_LABEL[range];
  const kpisWord = totalKpis === 1 ? "KPI" : "KPIs";
  const periodsWord = periodTotal === 1 ? "month" : "months";
  const failingKpisWord = failingPeriodKpiCount === 1 ? "KPI" : "KPIs";

  return (
    <div
      className="space-y-4"
      data-testid="kpi-summary-strip"
    >
      <section aria-labelledby="kpi-summary-current-period">
        <h3
          id="kpi-summary-current-period"
          className={OVERLINE_CLASS}
          data-testid="kpi-summary-current-overline"
        >
          This period · {currentPeriodLabel}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div
            className={CARD_CLASS}
            data-testid="kpi-summary-tile-onTrack"
          >
            <div className={CARD_LABEL_CLASS}>On track</div>
            <div
              className={`${CARD_VALUE_BASE} ${CARD_VALUE_NEUTRAL}`}
              data-testid="kpi-summary-value-onTrack"
            >
              {onTrack}
            </div>
            <div className={CARD_SUBVALUE_CLASS}>
              of {totalKpis} {kpisWord}
            </div>
          </div>
          <div
            className={CARD_CLASS}
            data-testid="kpi-summary-tile-atRisk"
          >
            <div className={CARD_LABEL_CLASS}>At risk</div>
            <div
              className={atRiskValueClass(atRisk)}
              data-testid="kpi-summary-value-atRisk"
            >
              {atRisk}
            </div>
            <div className={CARD_SUBVALUE_CLASS}>
              of {totalKpis} {kpisWord}
            </div>
          </div>
          <div
            className={CARD_CLASS}
            data-testid="kpi-summary-tile-failing"
          >
            <div className={CARD_LABEL_CLASS}>Failing</div>
            <div
              className={failingValueClass(failing)}
              data-testid="kpi-summary-value-failing"
            >
              {failing}
            </div>
            <div className={CARD_SUBVALUE_CLASS}>
              of {totalKpis} {kpisWord}
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="kpi-summary-trend">
        <h3
          id="kpi-summary-trend"
          className={OVERLINE_CLASS}
          data-testid="kpi-summary-trend-overline"
        >
          {rangeOverline}
        </h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div
            className={CARD_CLASS}
            data-testid="kpi-summary-tile-complianceRate"
          >
            <div className={`${CARD_LABEL_CLASS} flex items-center gap-1`}>
              Compliance rate
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400 hover:text-slate-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-500 dark:hover:text-slate-300"
                aria-label="About compliance weighting"
                title="Amber counts as partial credit (50%)"
                data-testid="kpi-summary-compliance-help"
              >
                <HelpCircle aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </div>
            <div
              className={complianceRateValueClass(overallPercent)}
              data-testid="kpi-summary-value-complianceRate"
            >
              {overallPercent === null ? "—" : `${overallPercent}%`}
            </div>
            <div className={CARD_SUBVALUE_CLASS}>
              {periodHits} of {periodTotal} {periodsWord} hit target
            </div>
          </div>
          <div
            className={CARD_CLASS}
            data-testid="kpi-summary-tile-failingPeriods"
          >
            <div className={`${CARD_LABEL_CLASS} flex items-center gap-1`}>
              Failing months
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400 hover:text-slate-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-500 dark:hover:text-slate-300"
                aria-label="About failing months"
                title="Number of months where any KPI was rated red."
                data-testid="kpi-summary-failing-help"
              >
                <HelpCircle aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </div>
            <div
              className={failingValueClass(failingPeriods)}
              data-testid="kpi-summary-value-failingPeriods"
            >
              {failingPeriods}
            </div>
            <div className={CARD_SUBVALUE_CLASS}>
              across {failingPeriodKpiCount} {failingKpisWord} ·{" "}
              {failingInLastQuarter} in last quarter
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
